import firebase from 'firebase/app';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ToastVariant } from '../../components/Toast/toast.model';
import { toastService } from '../../components/Toast/toast.service';
import { firebaseAuth, firebaseFunctions, googleProvider, nextcloudProvider } from '../../firebase/firebase';
import { FirebaseCallableFunctions } from '../../firebase/firebase.models';
import { gtm } from '../../reportGTM';

import { AuthModal } from './AuthModal';
import { AuthDialog, ExtendedSignUpFormValues, SignInFormValues } from './auth.model';
export interface ModalConfig {
  title?: string;
  onSuccess?: () => any;
}
export interface ModalProps extends ModalConfig {
  content?: AuthDialog;
}

interface AuthContextValues {
  isAdmin: boolean;
  isCreator: boolean;
  isAuth: boolean;
  isAuthenticating: boolean;
  currentUser: firebase.User;
  signUp: (values: ExtendedSignUpFormValues) => Promise<firebase.auth.UserCredential>;
  signIn: (values: SignInFormValues) => Promise<firebase.auth.UserCredential>;
  signInWithGoogle: () => void;
  signInWithNextcloud: () => void;
  signOut: () => Promise<void>;
  sendPasswordResetEmail: (email: string) => Promise<void>;
  sendVerificationEmail: (email?: string) => Promise<{ status: string; message: string }>;
  showSignInModal: (config?: ModalConfig) => void;
  showSignUpModal: (config?: ModalConfig) => void;
  showForgotPasswordModal: () => void;
  closeModal: () => void;
  updateInformation: (name: string) => void;
  updateEmail: (email: string) => Promise<void>;
}

const AuthContext = React.createContext<AuthContextValues>(null);
const onNewUser = firebaseFunctions.httpsCallable(FirebaseCallableFunctions.ON_NEW_USER);

export const AuthProvider: React.FC = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<firebase.User>();
  const [isAuthenticating, setIsAuthenticating] = useState<boolean>(true);
  const [dialogContent, setDialogContent] = useState<ModalProps>();
  const [showDialog, setShowDialog] = useState(false);
  const { t } = useTranslation();
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [isCreator, setIsCreator] = useState<boolean>(false);

  async function signUp({
    email,
    password,
    firstName,
    lastName,
    marketingConsent,
  }: ExtendedSignUpFormValues): Promise<firebase.auth.UserCredential> {
    try {
      const userCredential = await firebaseAuth.createUserWithEmailAndPassword(email, password);
      const user = userCredential.user;
      // Pobierz dane kampanii z localStorage, nawet jeśli nie są w URL
      const campaignData = handleCampaign();
      if (user) {
        await onNewUser({
          firstName,
          lastName,
          email: user.email,
          uid: user.uid,
          marketingConsent,
          campaignData,
        });

        gtm('sign_up', {
          method: 'Email',
        });
      }

      return userCredential;
    } catch (error: any) {
      console.error('Error creating user in Firebase Authentication:', error);
      const message =
        error?.code === 'auth/email-already-in-use'
          ? t('AUTH.SIGN_UP.EMAIL_ALREADY_IN_USE')
          : t('AUTH.SIGN_UP.AUTH_ERROR');
      toastService.show(message, t('ERROR'), { variant: ToastVariant.ERROR });
      return null;
    }
  }

  function signIn({ email, password }: SignInFormValues): Promise<firebase.auth.UserCredential> {
    return firebaseAuth.signInWithEmailAndPassword(email, password).then((v) => {
      setShowDialog(false);
      gtm('login', {
        method: 'Email',
      });
      return v;
    });
  }

  function handleSuccessfulLogin(method) {
    gtm('login', {
      method: method,
    });
  }

  function signInWithGoogle() {
    return firebase
      .auth()
      .signInWithPopup(googleProvider)
      .then((v) => {
        if (v.additionalUserInfo.isNewUser) {
          showNonEmailSignUpModal();
          gtm('sign_up', {
            method: 'Google',
          });
        } else setShowDialog(false);
        handleSuccessfulLogin('Google');
        return v;
      });
  }

  function signInWithNextcloud() {
    return firebase
      .auth()
      .signInWithPopup(nextcloudProvider)
      .then((v) => {
        if (v.additionalUserInfo.isNewUser) {
          showNonEmailSignUpModal();
          gtm('sign_up', {
            method: 'Nextcloud',
          });
        } else setShowDialog(false);
        handleSuccessfulLogin('Nextcloud');
        return v;
      });
  }

  function signOut(): Promise<void> {
    return firebaseAuth.signOut();
  }
  function sendPasswordResetEmail(email: string): Promise<void> {
    return firebaseAuth.sendPasswordResetEmail(email);
  }

  async function sendVerificationEmail(email?: string): Promise<{ status: string; message: string }> {
    showVerifyEmailModal();
    const sendEmailVerificationCode = firebaseFunctions.httpsCallable(
      FirebaseCallableFunctions.SEND_EMAIL_VERIFICATION_CODE
    );
    try {
      const response = await sendEmailVerificationCode({ email: email ? email : currentUser.email });
      return response.data;
    } catch (error: any) {
      console.error('Error sending verification email:', error);
      return { status: 'ERROR', message: error.message || 'Wystąpił błąd podczas wysyłania e-maila.' };
    }
  }

  function showSignInModal(config: ModalConfig): void {
    setDialogContent({ ...config, content: AuthDialog.SIGN_IN });
    setShowDialog(true);
  }
  function showSignUpModal(config: ModalConfig): void {
    setDialogContent({ ...config, content: AuthDialog.SIGN_UP });
    setShowDialog(true);
  }
  function showNonEmailSignUpModal(): void {
    setDialogContent({ content: AuthDialog.NON_EMAIL_SIGN_UP });
    setShowDialog(true);
  }
  function showForgotPasswordModal(): void {
    setDialogContent({ content: AuthDialog.FORGOT_PASSWORD });
    setShowDialog(true);
  }
  function showVerifyEmailModal(): void {
    setDialogContent({ content: AuthDialog.VERIFY_EMAIL, title: t('AUTH.EMAIL.CONFIRMATION_TITLE') });
    setShowDialog(true);
  }
  function closeModal(): void {
    setShowDialog(false);
  }
  function updateInformation(name: string) {
    firebase.auth().currentUser.updateProfile({ displayName: name });
  }
  function updateEmail(email: string): Promise<void> {
    return firebaseAuth.currentUser.updateEmail(email);
  }

  useEffect(() => {
    setIsAuthenticating(true);
    const unsubscribe = firebaseAuth.onAuthStateChanged(async (user) => {
      setCurrentUser(user);
      if (user) {
        const tokenResult = await user.getIdTokenResult();
        setIsAdmin(!!tokenResult.claims.admin);
        setIsCreator(!!tokenResult.claims.creator);
      } else {
        setIsAdmin(false);
        setIsCreator(false);
      }
      setIsAuthenticating(false);
    });
    return () => unsubscribe();
  }, []);

  const values: AuthContextValues = {
    isAdmin: isAdmin,
    isCreator: isCreator,
    isAuth: !!currentUser?.email,
    isAuthenticating,
    currentUser,
    signUp,
    signIn,
    signInWithGoogle,
    signInWithNextcloud,
    signOut,
    sendPasswordResetEmail,
    sendVerificationEmail,
    showSignInModal,
    showSignUpModal,
    showForgotPasswordModal,
    closeModal,
    updateInformation,
    updateEmail,
  };

  return (
    <AuthContext.Provider value={values}>
      {children}
      <AuthModal dialogConfig={dialogContent} showDialog={showDialog} />
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextValues => {
  return useContext(AuthContext);
};

// Stała dla nazwy klucza w localStorage
export const UTM_STORAGE_KEY = 'najmuje_utm_params';
// Czas życia UTM params w milisekundach (30 dni)
const UTM_EXPIRY_TIME = 30 * 24 * 60 * 60 * 1000;

// Funkcja zapisująca parametry UTM do localStorage z datą wygaśnięcia
const saveUtmParamsToStorage = (params: any) => {
  const dataToStore = {
    params,
    expiry: new Date().getTime() + UTM_EXPIRY_TIME,
  };
  localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(dataToStore));
};

// Funkcja odczytująca parametry UTM z localStorage (wraz z weryfikacją ważności)
const getUtmParamsFromStorage = (): any => {
  try {
    const storedData = localStorage.getItem(UTM_STORAGE_KEY);
    if (!storedData) return null;

    const { params, expiry } = JSON.parse(storedData);

    // Sprawdź czy dane nie wygasły
    if (new Date().getTime() > expiry) {
      localStorage.removeItem(UTM_STORAGE_KEY);
      return null;
    }

    return params;
  } catch (error) {
    console.error('Error getting UTM params from storage:', error);
    return null;
  }
};

export const handleCampaign = (): { [key: string]: string } | null => {
  const urlParams = new URLSearchParams(window.location.search);
  const utmSource = urlParams.get('utm_source');
  const utmMedium = urlParams.get('utm_medium');
  const utmCampaign = urlParams.get('utm_campaign');
  const utmId = urlParams.get('utm_id');
  const utmTerm = urlParams.get('utm_term');
  const utmContent = urlParams.get('utm_content');

  // Sprawdź czy są wszystkie parametry kampanii w URL
  if (utmSource && utmMedium && utmCampaign && utmId && utmTerm && utmContent) {
    // Mamy pełne dane UTM w URL - zapisz je do localStorage
    const campaignData = {
      utmSource,
      utmMedium,
      utmCampaign,
      utmId,
      utmTerm,
      utmContent,
      timestamp: new Date().toISOString(),
    };

    saveUtmParamsToStorage(campaignData);
    return campaignData;
  }

  // Brak pełnych parametrów UTM w URL - spróbuj odczytać z localStorage
  const storedParams = getUtmParamsFromStorage();
  if (storedParams) {
    // Aktualizujemy timestamp, aby odzwierciedlał aktualny czas
    return {
      ...storedParams,
      timestamp: new Date().toISOString(),
    };
  }

  // Brak parametrów UTM zarówno w URL jak i w localStorage
  return null;
};
